import { useQuery } from '@tanstack/react-query';
import type { MotornetVehicleResponse, MotornetVehicleKind } from '../types';
import { useApiClient } from 'src/hooks/useApiClient';

type ApiClient = ReturnType<typeof useApiClient>['apiClient'];

/**
 * Opzioni di query condivise: chi deve caricare piu' veicoli fuori da un
 * componente (es. l'import Excel) passa da qui, cosi' la cache e' la stessa di
 * `useMotornetVehicle` e un veicolo gia' visto non viene richiesto due volte.
 */
export const motornetVehicleQueryOptions = (
	apiClient: ApiClient,
	eurotaxCode: string,
	vehicleKind: MotornetVehicleKind = 'auto',
	year: number | null = null,
	month: number | null = null,
	overrides: { retry?: boolean | number; staleTime?: number } = {}
) => {
	const code = eurotaxCode.trim();
	return {
		queryKey: ['motornet', 'vehicle', code, vehicleKind, year, month],
		queryFn: (): Promise<MotornetVehicleResponse> =>
			apiClient.motornetControllerService.getMotornetVehicle(code, vehicleKind, {
				year: year ?? undefined,
				month: month ?? undefined,
			}),
		staleTime: 5 * 60 * 1000,
		retry: false,
		refetchOnWindowFocus: false,
		refetchOnReconnect: false,
		...overrides,
	};
};

export const useMotornetVehicle = (
	eurotaxCode: string | null,
	vehicleKind: MotornetVehicleKind = 'auto',
	year: number | null = null,
	month: number | null = null
) => {
	const { apiClient } = useApiClient();
	const code = eurotaxCode?.trim() ?? '';

	return useQuery<MotornetVehicleResponse>({
		...motornetVehicleQueryOptions(apiClient, code, vehicleKind, year, month),
		enabled: code.length > 0,
	});
};
